import React, { useState } from 'react';
import classnames from 'classnames';
import { useMemoizedFn } from 'ahooks';
import { Form, Radio, Icon, Button, BubbleProps } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import InfoBubble from '@gp/components/InfoBubble';
import GeneralModal from '@gp/components/GeneralModal';
import { noop } from '@gp/utils/common';
import { FILE_TYPES1, FILE_TYPES_MAP } from '@gp/constants/const';

import './index.scss';

interface IGeneralDownloadBtnProps extends IBaseProps {
  isLoading?: boolean;
  isDisabled?: boolean;
  tipsPlacement?: BubbleProps['placement'];
  tips?: string | React.ReactElement;
  uiType?: '1' | '2';
  fileTypes?: ISelectOption[];
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onConfirm?: (downloadFormModel: any) => void;
}

const getInitialFileType = (fileTypes: ISelectOption[]): string => {
  if (!Array.isArray(fileTypes) || !fileTypes.length) return '';
  const csvFileType = fileTypes.find((item) => item?.value === FILE_TYPES_MAP.CSV);
  return csvFileType?.value || fileTypes[0]?.value || '';
};

const GeneralDownloadBtn: React.FC<IGeneralDownloadBtnProps> = (props) => {
  const {
    style,
    className,
    isLoading = false,
    isDisabled = false,
    tipsPlacement = 'top',
    tips,
    uiType = '1',
    fileTypes: fileTypesProps,
    onClick,
    onOpen,
    onClose,
    onConfirm = noop,
    ...remains
  } = props;

  const fileTypes = Array.isArray(fileTypesProps) ? fileTypesProps : FILE_TYPES1;

  const initialDownloadFormModel = {
    fileType: getInitialFileType(fileTypes),
  };
  const [visible, setVisible] = useState(false);
  const [downloadFormModel, setDownloadFormModel] = useState(initialDownloadFormModel);

  const open = useMemoizedFn(() => {
    setVisible(true);
    onOpen?.();
  });

  const close = useMemoizedFn(() => {
    setVisible(false);
    onClose?.();
    setTimeout(() => {
      setDownloadFormModel(initialDownloadFormModel);
    }, 300);
  });

  const handleDownloadFormChange = useMemoizedFn((key: string, value: any) => {
    const newDownloadFormModel = {
      ...downloadFormModel,
      fileType: value,
    };
    setDownloadFormModel(newDownloadFormModel);
  });

  const handleClick = useMemoizedFn((e: any) => {
    e?.stopPropagation();
    if (isDisabled) return;
    if (isLoading) return;
    open();
  });

  const handleConfirm = useMemoizedFn(() => {
    if (isDisabled) return;
    onConfirm(downloadFormModel);
    close();
  });

  const handleCancel = useMemoizedFn(() => {
    close();
  });

  return (
    <>
      <InfoBubble
        style={{ display: 'inline-block' }}
        className="globalpedia-modal-font14"
        isArrowPointAtCenter
        placement={tipsPlacement}
        customBubbleContent={tips}
        DomOrText={
          <Button
            {...remains}
            style={{
              cursor: isDisabled || isLoading ? 'not-allowed' : '',
              ...style,
            }}
            className={classnames('dbg-general-download-btn ns-btn--icon', className, {
              'is-disabled': isDisabled,
              'ui-type1': uiType === '1',
              'ui-type2': uiType === '2',
            })}
            // @ts-ignore
            onClick={isDisabled || isLoading ? noop : !!onClick ? onClick : handleClick}
          >
            {isLoading && <Icon type="loading" />}
            {isLoading || (
              <SvgIcon style={{ verticalAlign: 'bottom' }} className="dbg-general-download-btn-icon" type="download" />
            )}
          </Button>
        }
      />
      <GeneralModal
        className={classnames('dbg-general-download-modal', { 'dbg-general-hidden': fileTypes?.length <= 6 })}
        title="Download"
        modalSize="m"
        isFont14={false}
        visible={visible}
        onConfirm={handleConfirm}
        onClose={handleCancel}
        content={
          <Form className="dbg-general-download-form teaui-form-alignment">
            {props.children}
            <Form.Item label="File Format">
              <Radio.Group
                value={downloadFormModel?.fileType}
                onChange={(value) => {
                  handleDownloadFormChange('fileType', value);
                }}
              >
                {fileTypes?.map((item) => (
                  <Radio key={item?.value} name={item?.value}>
                    {item?.text}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </Form>
        }
      />
    </>
  );
};

export default React.memo(GeneralDownloadBtn);
